import {
    Alert,
    Button,
    Card,
    Col,
    Flex,
    Form,
    Input,
    Radio,
    Row,
    Select,
    Spin,
    Steps,
    Tag,
    theme,
    Typography,
    Upload,
    UploadFile,
    UploadProps,
} from "antd";
import {
    CarOutlined,
    CheckOutlined,
    CheckCircleFilled,
    DeleteOutlined,
    EditOutlined,
    FileTextOutlined,
    IdcardOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    MessageOutlined,
    RightCircleOutlined,
    SearchOutlined,
    SendOutlined,
    SettingOutlined,
    PaperClipOutlined,
    ToolOutlined,
} from "@ant-design/icons";
import { useDelete } from "@refinedev/core";
import { useEffect, useMemo, useState } from "react";
import FormRender from "../../forms/FormRender";
import { MasterSlaveOptions, MethodOption, SimpleMethodOptions, TechniqueOptions } from "../../../models/automation";
import { BackButton } from "../../../components/backButton";
import { CustomerBackButton } from "./customerBackButton";
import { usePageStyles } from "../../../shared/styles";
import { axiosInstance } from "../../../authProvider";
import { useDeductLocalAccountCredit } from "../../../hooks/account";
import {
    AnalysisFormUpdates,
    FILE_READING_FIELD_NAMES,
    getFileReadingPreset,
    getFormUpdatesFromAnalysis,
    stripReadingFieldsFromUpdates,
} from "../utils/analysisFormAutofill";
import { getDevOrderFormValues } from "../utils/devOrderFormAutofill";
import { collectFormFieldNamesFromSchema } from "../../forms/utils/collect-form-field-names";

const STEP_FILE = 0;
const STEP_VEHICLE = 1;
const STEP_STAGE_OPTIONS = 2;
const STEP_SUMMARY = 3;

const methodRadioOptions = SimpleMethodOptions.map((o: MethodOption) => ({ label: o.title, value: o.value }));
const techniqueRadioOptions = TechniqueOptions.map((o: MethodOption) => ({ label: o.title, value: o.value }));
const masterSlaveOptions = MasterSlaveOptions.map((o: MethodOption) => ({ label: o.title, value: o.value }));

const requiresDecryptAndAnalyze = (
    method: string | undefined,
    masterslave: string | undefined
) => masterslave === "slave" && (method === "bflash" || method === "autotuner");

const STEP_FILE_FIELD_NAMES = new Set<string>(FILE_READING_FIELD_NAMES);
const STEP_FILE_FORM_FIELD_NAMES = FILE_READING_FIELD_NAMES;

/** Schema for step 2 — excludes method/technique/masterslave (rendered on step 1). */
const filterVehicleFormSchema = (schema: any[]): any[] =>
    schema
        .map((container) => {
            if (!container.children?.length) {
                return STEP_FILE_FIELD_NAMES.has(container.name) ? null : container;
            }
            const children = container.children.filter(
                (child: any) => !STEP_FILE_FIELD_NAMES.has(child.name)
            );
            if (children.length === 0) return null;
            return { ...container, children };
        })
        .filter(Boolean);

const { Dragger } = Upload;

export interface OrderCreateSteppedProps {
    formSchema: any[];
    formId: string;
    user: any;
    selectedAccountId: number | undefined;
    setSelectedAccountId: (id: number | undefined) => void;
    accountSelectProps: any;
    isAdmin: boolean;
    accountIdFromParams: number | undefined;
    isPrepaid: boolean;
    accountCredit: number;
    open: (params: { message: string; type: "error" | "success"; key?: string; duration?: number }) => void;
    _t: (key: string) => string;
    mutate: (params: any, options?: any) => void;
    mutateMany: (params: any, options?: any) => void;
    apiUrl: string;
    setSuccess: (v: boolean) => void;
    onOrderCreated: (orderId: number) => void;
    token: ReturnType<typeof theme.useToken>["token"];
    onBuyModalOpen: () => void;
    productQueryResult: { data?: { data?: any[] } };
    productList: { options?: { label: number; value: number }[] };
    onAccountCreditUpdated?: (credit: number) => void;
}

export const OrderCreateStepped = (props: OrderCreateSteppedProps) => {
    const {
        formSchema,
        user,
        selectedAccountId,
        setSelectedAccountId,
        accountSelectProps,
        isAdmin,
        accountIdFromParams,
        isPrepaid,
        accountCredit,
        open,
        _t,
        mutate,
        mutateMany,
        apiUrl,
        setSuccess,
        onOrderCreated,
        token,
        onBuyModalOpen,
        productQueryResult,
        productList,
        onAccountCreditUpdated,
    } = props;

    const deductLocalCredit = useDeductLocalAccountCredit();
    const { mutateAsync: deleteFile } = useDelete();
    const { token: antdToken } = theme.useToken();
    const pageStyles = usePageStyles();
    const [currentStep, setCurrentStep] = useState(0);
    const [mainFileList, setMainFileList] = useState<UploadFile[]>([]);
    const [additionalFileList, setAdditionalFileList] = useState<UploadFile[]>([]);
    const [showAdditionalFilesPanel, setShowAdditionalFilesPanel] = useState(false);
    const [comments, setComments] = useState("");
    const [selectedStageIds, setSelectedStageIds] = useState<number[]>([]);
    const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
    const [stageSearch, setStageSearch] = useState("");
    const [optionsSearch, setOptionsSearch] = useState("");
    const [analysisFormValues, setAnalysisFormValues] = useState<AnalysisFormUpdates | null>(
        null
    );
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [isSendingOrder, setIsSendingOrder] = useState(false);
    const [lastAnalyzedFileId, setLastAnalyzedFileId] = useState<string | null>(null);
    const [isMainFileAnalyzing, setIsMainFileAnalyzing] = useState(false);
    const [isValidatingStep, setIsValidatingStep] = useState(false);
    const [form] = Form.useForm();
    const method = Form.useWatch("method", form);
    const masterslave = Form.useWatch("masterslave", form);

    const vehicleFormSchema = useMemo(
        () => filterVehicleFormSchema(formSchema),
        [formSchema]
    );

    const applyVehicleFormValues = (updates: AnalysisFormUpdates) => {
        if (Object.keys(updates).length === 0) return;
        form.setFieldsValue(updates);
        queueMicrotask(() => form.setFieldsValue(updates));
    };

    const applyAnalysisFormValues = (updates: AnalysisFormUpdates | Record<string, string>) => {
        const vehicleUpdates = stripReadingFieldsFromUpdates(updates);
        if (Object.keys(vehicleUpdates).length === 0) return;
        applyVehicleFormValues(vehicleUpdates);
        setAnalysisFormValues(vehicleUpdates);
    };

    const applyFileReadingPreset = (fileName: string | undefined) => {
        if (!fileName) return;
        const preset = getFileReadingPreset(fileName);
        if (preset) {
            form.setFieldsValue(preset);
        }
    };

    const products: any[] = productQueryResult?.data?.data ?? [];
    const stages = useMemo(() => products.filter((p: any) => (p.type || "").toLowerCase() === "base"), [products]);
    const productGroups = useMemo(() => {
        const groups = new Set<string>();
        products.forEach((p: any) => {
            const groupName = (p.group || "").toString();
            groups.add(groupName);
        });
        return Array.from(groups).sort((a, b) => a.localeCompare(b));
    }, [products]);
    const optionsByGroup = useMemo(() => {
        const options = products.filter((p: any) => (p.type || "").toLowerCase() === "option");
        const byGroup: Record<string, any[]> = {};
        options.forEach((p: any) => {
            const g = p.group || "";
            if (!byGroup[g]) byGroup[g] = [];
            byGroup[g].push(p);
        });
        return Object.entries(byGroup).sort(([a], [b]) => a.localeCompare(b));
    }, [products]);

    const getProductPrice = (productId: number): number => {
        const product = productQueryResult?.data?.data?.find((p: any) => p.id === productId) ?? null;
        if (!product) {
            console.error("Product with id ${productId} not found");
            return -1;
        }
        return product.price;
    };

    const totalCredits = useMemo(() => {
        let t = 0;
        selectedStageIds.forEach((id) => (t += getProductPrice(id)));
        selectedOptionIds.forEach((id) => (t += getProductPrice(id)));
        return t;
    }, [selectedStageIds, selectedOptionIds, productList]);

    const filteredStages = useMemo(() => {
        const search = stageSearch.trim().toLowerCase();
        return stages.filter((p: any) => {
            const matchesGroup =
                selectedGroup == null ? true : (p.group || "").toString() === selectedGroup;
            const matchesSearch = !search
                || (p.name || "").toLowerCase().includes(search);
            return matchesGroup && matchesSearch;
        });
    }, [stages, stageSearch, selectedGroup]);

    const filteredOptionsByGroup = useMemo(() => {
        const search = optionsSearch.trim().toLowerCase();
        if (!selectedGroup) {
            return [];
        }

        return optionsByGroup
            .filter(([groupName]) => groupName === selectedGroup)
            .map(
                ([groupName, opts]) =>
                    [
                        groupName,
                        search
                            ? opts.filter((p: any) =>
                                (p.name || "").toLowerCase().includes(search)
                            )
                            : opts,
                    ] as [string, any[]]
            )
            .filter(([, opts]) => opts.length > 0);
    }, [optionsByGroup, optionsSearch, selectedGroup]);

    const getFileIdFromResponse = (response: any): string | undefined =>
        response?.file?.id ?? response?.data?.file?.id;

    const mainFile = useMemo((): { name: string; fileId: string; metadata: string } | null => {
        const done = mainFileList.find((f) => f.status === "done" && getFileIdFromResponse(f.response));
        if (!done) return null;
        const fileId = getFileIdFromResponse(done.response);
        if (!fileId) return null;
        return {
            name: done.name,
            fileId,
            metadata: JSON.stringify({ type: done.type, size: done.size, category: "bin" }),
        };
    }, [mainFileList]);

    const activeReadingPreset = useMemo(
        () => getFileReadingPreset(mainFileList[0]?.name ?? mainFile?.name ?? ""),
        [mainFileList, mainFile?.name]
    );

    const isMainFileUploading = mainFileList.some((f) => f.status === "uploading");
    const isMainFileBusy = isMainFileUploading || isMainFileAnalyzing;

    useEffect(() => {
        const fileId = mainFile?.fileId;
        if (!fileId) {
            setAnalysisFormValues(null);
            return;
        }

        if (lastAnalyzedFileId === fileId) return;

        setIsMainFileAnalyzing(true);

        const fileName = mainFile.name;
        const readingPreset = getFileReadingPreset(fileName);
        applyFileReadingPreset(fileName);

        const effectiveMethod = readingPreset?.method ?? method;
        const effectiveMasterslave = readingPreset?.masterslave ?? masterslave;
        const useDecryptAndAnalyze = requiresDecryptAndAnalyze(
            effectiveMethod,
            effectiveMasterslave
        );

        let cancelled = false;

        (async () => {
            try {
                let response;
                if (useDecryptAndAnalyze) {
                    const doneFile = mainFileList.find(
                        (f) =>
                            f.status === "done" &&
                            getFileIdFromResponse(f.response) === fileId
                    );
                    const rawFile = doneFile?.originFileObj as File | undefined;
                    if (!rawFile) {
                        if (!cancelled) {
                            console.warn(
                                "Main file analysis: missing file blob for decrypt-and-analyze"
                            );
                        }
                        return;
                    }
                    const formData = new FormData();
                    formData.append("file", rawFile);
                    formData.append("method", effectiveMethod!);
                    response = await axiosInstance.post(
                        "/file-automation/decrypt-and-analyze",
                        formData
                    );
                } else {
                    response = await axiosInstance.post("/file-analyzer/analyze", {
                        fileId,
                    });
                }

                if (!cancelled) {
                    console.log("Main file analysis:", response.data);
                    const formUpdates = getFormUpdatesFromAnalysis(response.data);
                    applyAnalysisFormValues(formUpdates);
                    setLastAnalyzedFileId(fileId);
                }
            } catch (error) {
                if (!cancelled) {
                    console.warn("Main file analysis failed:", error);
                }
            } finally {
                if (!cancelled) {
                    setIsMainFileAnalyzing(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            setIsMainFileAnalyzing(false);
        };
    }, [
        mainFile?.fileId,
        mainFile?.name,
        method,
        masterslave,
        mainFileList,
        form,
        lastAnalyzedFileId,
    ]);

    const additionalFiles = useMemo((): { name: string; fileId: string; metadata: string }[] => {
        return additionalFileList
            .filter((f) => f.status === "done" && getFileIdFromResponse(f.response))
            .map((f) => {
                const fileId = getFileIdFromResponse(f.response)!;
                return {
                    name: f.name,
                    fileId,
                    metadata: JSON.stringify({ type: f.type, size: f.size, category: "misc" }),
                };
            });
    }, [additionalFileList]);

    const mainFileUploadProps: UploadProps = {
        name: "file",
        action: apiUrl + "/files/upload",
        method: "POST",
        maxCount: 1,
        fileList: mainFileList,
        showUploadList: false,
        onChange(info) {
            setMainFileList(info.fileList.slice(0, 1));
            if (info.file.name) {
                applyFileReadingPreset(info.file.name);
            }
        },
    };

    const additionalFilesUploadProps: UploadProps = {
        name: "file",
        action: apiUrl + "/files/upload",
        method: "POST",
        fileList: additionalFileList,
        showUploadList: false,
        onChange(info) {
            setAdditionalFileList(info.fileList);
        },
    };

    const deleteFileFromServer = (fileId: string) =>
        deleteFile({ resource: "files", id: fileId });

    const canGoNextStep1 = mainFile != null;
    const hasSelectedProducts =
        selectedStageIds.length > 0 || selectedOptionIds.length > 0;
    const canGoNextStepStageOptions = hasSelectedProducts;
    const canSubmit =
        hasSelectedProducts &&
        mainFile != null &&
        (!isPrepaid || totalCredits <= accountCredit) &&
        (!isAdmin || accountIdFromParams || selectedAccountId != null);

    const accountIdToUse = selectedAccountId ?? user?.account?.id;

    const buildPayload = () => {
        const formValues = form.getFieldsValue(true);
        const readingPreset = getFileReadingPreset(mainFile?.name ?? "");
        const values = readingPreset ? { ...formValues, ...readingPreset } : formValues;
        console.log("form data values", values);

        const payload = formSchema.map((item: any) => {
            if (item.children?.length > 0) {
                return {
                    ...item,
                    children: item.children.map((child: any) => ({
                        ...child,
                        value: values[child.name],
                    })),
                };
            }
            return { ...item, value: values[item.name] };
        });
        const step1Fields = ["method", "technique", "masterslave", "calculator", "prepared"] as const;
        step1Fields.forEach((name) => {
            payload.push({ name, value: values[name] });
        });
        return payload;
    };

    const notifyValidationFailed = () => {
        open({
            message: _t("orders.stepped.validationRequired"),
            type: "error",
            key: "stepped-validation",
        });
    };

    const getAllFormFieldNames = (): string[] => [
        ...new Set([
            ...STEP_FILE_FORM_FIELD_NAMES,
            ...collectFormFieldNamesFromSchema(formSchema),
        ]),
    ];

    const handleSubmit = async () => {
        if (!accountIdToUse) {
            open({
                message: _t("orders.fields.account") + " " + _t("buttons.notAccessTitle"),
                type: "error",
                key: "account-required",
            });
            return;
        }
        const allNames = getAllFormFieldNames();
        if (allNames.length > 0) {
            try {
                await form.validateFields(allNames);
            } catch {
                notifyValidationFailed();
                return;
            }
        }
        setIsSendingOrder(true);
        const productIds = [...new Set([...selectedStageIds, ...selectedOptionIds].map(String))];
        const order = {
            productIds,
            accountId: String(accountIdToUse),
            createdBy: String(user?.id),
            payload: JSON.stringify(buildPayload()),
            status: "submitted",
            comments,
        };
        mutate(
            { resource: "orders", values: order },
            {
                onSuccess: (data: any) => {
                    const orderEntity = data?.data;
                    if (isPrepaid && accountIdToUse) {
                        const newCredit = deductLocalCredit(totalCredits, accountIdToUse);
                        if (newCredit !== null) {
                            onAccountCreditUpdated?.(newCredit);
                        }
                    }
                    if (orderEntity?.id != null) {
                        onOrderCreated(Number(orderEntity.id));
                    }
                    const allFiles = [...(mainFile ? [mainFile] : []), ...additionalFiles];
                    const attachmentList = allFiles.map((file) => ({
                        name: file.name,
                        fileId: file.fileId,
                        metadata: file.metadata,
                        orderId: String(orderEntity?.id),
                        direction: "in",
                    }));
                    if (attachmentList.length > 0) {
                        mutateMany(
                            { resource: "attachments", values: attachmentList },
                            {
                                onSuccess: () => setSuccess(true),
                                onError: () => {
                                    setIsSendingOrder(false);
                                    open({
                                        message: _t("orders.errors.createAttachment"),
                                        type: "error",
                                        key: "stepped-attachment",
                                        duration: 0,
                                    });
                                },
                            }
                        );
                    } else {
                        setSuccess(true);
                    }
                },
                onError: () => setIsSendingOrder(false),
            }
        );
    };

    const stepItems = [
        { title: _t("orders.stepped.stepFile") },
        { title: _t("orders.stepped.stepVehicle") },
        { title: _t("orders.stepped.stepStageOptions") },
        { title: _t("orders.stepped.stepSummary") },
    ];

    const handleNextStep = async (): Promise<void> => {
        try {
            setIsValidatingStep(true);
            if (currentStep === STEP_FILE) {
                if (!canGoNextStep1 || isMainFileBusy) {
                    return;
                }
                applyFileReadingPreset(mainFileList[0]?.name ?? mainFile?.name);
                await form.validateFields([...STEP_FILE_FORM_FIELD_NAMES]);
            } else if (currentStep === STEP_VEHICLE) {
                const names = collectFormFieldNamesFromSchema(vehicleFormSchema);
                if (names.length > 0) {
                    await form.validateFields(names);
                }
            } else if (currentStep === STEP_STAGE_OPTIONS) {
                if (!canGoNextStepStageOptions) {
                    open({
                        message: _t("orders.stepped.productSelectionRequired"),
                        type: "error",
                        key: "stepped-product-selection",
                    });
                    return;
                }
            }
            setCurrentStep((s) => s + 1);
        } catch {
            notifyValidationFailed();
        } finally {
            setIsValidatingStep(false);
        }
    };

    useEffect(() => {
        if (import.meta.env.PROD || formSchema.length === 0) return;
        form.setFieldsValue(getDevOrderFormValues(formSchema));
    }, [formSchema, form]);

    useEffect(() => {
        if (!analysisFormValues || vehicleFormSchema.length === 0) return;
        applyVehicleFormValues(analysisFormValues);
    }, [analysisFormValues, vehicleFormSchema]);

    useEffect(() => {
        if (currentStep === STEP_VEHICLE && analysisFormValues) {
            applyVehicleFormValues(analysisFormValues);
        }
    }, [currentStep, analysisFormValues]);

    useEffect(() => {
        if (!selectedGroup && productGroups.length > 0) {
            setSelectedGroup(productGroups[0]);
        }
    }, [productGroups, selectedGroup]);

    const methodValue = activeReadingPreset?.method ?? form.getFieldValue("method");
    const selectedMethodLabel =
        methodRadioOptions.find((option) => option.value === methodValue)?.label ?? methodValue ?? "—";
    const techniqueValue = form.getFieldValue("technique");
    const selectedTechniqueLabel =
        techniqueRadioOptions.find((option) => option.value === techniqueValue)?.label ?? techniqueValue ?? "—";
    const masterslaveValue = activeReadingPreset?.masterslave ?? form.getFieldValue("masterslave");
    const selectedMasterSlaveLabel =
        masterSlaveOptions.find((option) => option.value === masterslaveValue)?.label ?? masterslaveValue ?? "—";

    const vehicleMake = form.getFieldValue("make");
    const vehicleModel = form.getFieldValue("model");
    const vehicleYear = form.getFieldValue("year");
    const vehicleTitle =
        [vehicleMake, vehicleModel].filter(Boolean).join(" ") || _t("orders.stepped.summaryVehicleUnknown");

    const selectedStages = selectedStageIds
        .map((id) => stages.find((stage: any) => stage.id === id))
        .filter(Boolean) as any[];

    const selectedOptions = selectedOptionIds
        .map((id) => products.find((product: any) => product.id === id))
        .filter(Boolean) as any[];

    const primaryStage = selectedStages[0];
    const remainingStages = selectedStages.slice(1);
    const primaryFile = mainFile;
    const additionalFilesCount = additionalFiles.length;
    const selectedProductsBreakdown = [...selectedStageIds, ...selectedOptionIds]
        .map((id) => {
            const product = products.find((p: any) => p.id === id);
            if (!product) return null;
            const price = getProductPrice(id);
            if (!isPrepaid) return product.name;
            return `${product.name} (${price === 0 ? _t("orders.stepped.creditsFree") : price})`;
        })
        .filter(Boolean)
        .join(" + ");

    const vehicleSpecs = [
        {
            key: "power",
            label: _t("orders.stepped.specPower"),
            value: form.getFieldValue("power"),
        },
        {
            key: "mileage",
            label: _t("orders.stepped.specMileage"),
            value: form.getFieldValue("mileage")
                ? `${form.getFieldValue("mileage")} km`
                : undefined,
        },
        {
            key: "transmission",
            label: _t("orders.stepped.specTransmission"),
            value: form.getFieldValue("gearbox") ?? form.getFieldValue("transmission"),
        },
        {
            key: "cylinders",
            label: _t("orders.stepped.specCylinders"),
            value: form.getFieldValue("cylinders"),
        },
        {
            key: "engine",
            label: _t("orders.stepped.specEngine"),
            value: form.getFieldValue("engine"),
        },
    ].filter((spec) => spec.value != null && String(spec.value).trim() !== "");

    const SummarySectionHeader = ({
        icon,
        title,
        onEdit,
    }: {
        icon: React.ReactNode;
        title: string;
        onEdit?: () => void;
    }) => (
        <Flex justify="space-between" align="center" gap={12}>
            <Flex align="center" gap={10} className="min-w-0">
                <span
                    className="flex items-center justify-center rounded-lg shrink-0"
                    style={{
                        width: 32,
                        height: 32,
                        color: antdToken.colorPrimary,
                        background: antdToken.colorPrimaryBg,
                        border: `1px solid ${antdToken.colorPrimaryBorder}`,
                    }}
                >
                    {icon}
                </span>
                <Typography.Text
                    strong
                    className="uppercase tracking-wider text-xs truncate"
                    style={{ color: antdToken.colorText }}
                >
                    {title}
                </Typography.Text>
            </Flex>
            {onEdit && (
                <Button type="default" variant="outlined" icon={<EditOutlined />} onClick={onEdit}>
                    {_t("buttons.edit")}
                </Button>
            )}
        </Flex>
    );

    return (
        <Flex vertical className="w-full xl:max-w-[70%] mx-auto pb-28">
            <Flex className="my-2">
                {isAdmin ? <BackButton /> : <CustomerBackButton />}
                <Typography.Title level={4}>{_t("orders.titles.create")}</Typography.Title>
            </Flex>

            {isAdmin && !accountIdFromParams && (
                <Card className="mb-4" style={pageStyles.stepperCard}>
                    <Select
                        placeholder={_t("orders.fields.account")}
                        className="w-full max-w-xs"
                        {...accountSelectProps}
                        value={selectedAccountId}
                        onChange={(value) => setSelectedAccountId(value)}
                        suffixIcon={<IdcardOutlined />}
                    />
                </Card>
            )}

            <Steps current={currentStep} items={stepItems} className="mb-6" />

            <Form form={form} layout="vertical">
                {/* Step 1: File upload + reading method */}
                {currentStep === STEP_FILE && (
                    <div className="relative">
                        {isMainFileBusy && (
                            <div
                                className="absolute inset-0 z-20 flex items-center justify-center rounded-lg"
                                style={{
                                    backgroundColor: `${antdToken.colorBgContainer}cc`,
                                }}
                                aria-busy="true"
                                aria-live="polite"
                            >
                                <Spin
                                    size="large"
                                    tip={
                                        isMainFileUploading
                                            ? _t("orders.stepped.mainFileUploading")
                                            : _t("orders.stepped.mainFileAnalyzing")
                                    }
                                />
                            </div>
                        )}
                        <Card
                            className={`shadow-lg ${isMainFileBusy ? "pointer-events-none select-none" : ""}`}
                            style={pageStyles.stepperCard}
                        >
                        <Form.Item name="method">
                            <Radio.Group
                                optionType="button"
                                options={methodRadioOptions}
                            />
                        </Form.Item>

                        <Flex gap={16} wrap="wrap" align="start" className="mb-6">
                            <Form.Item name="technique" className="!mb-0">
                                <Radio.Group
                                    optionType="button"
                                    options={techniqueRadioOptions}
                                />
                            </Form.Item>
                            <Form.Item name="masterslave" initialValue="slave" className="!mb-0">
                                <Radio.Group optionType="button" options={masterSlaveOptions} />
                            </Form.Item>
                        </Flex>

                        <Form.Item
                            label={
                                <Flex gap={8} align="center">
                                    <FileTextOutlined style={{ color: token.colorPrimary }} />
                                    {_t("orders.stepped.mainFile")}
                                </Flex>
                            }
                        >
                            <Flex vertical gap={12}>
                                {mainFile && (
                                    <Flex
                                        align="center"
                                        gap={8}
                                        className="rounded-lg border px-4 py-3"
                                        style={{
                                            borderColor: antdToken.colorSuccessBorder,
                                            backgroundColor: antdToken.colorSuccessBg,
                                        }}
                                    >
                                        <CheckCircleFilled style={{ color: antdToken.colorSuccess, fontSize: 18 }} />
                                        <Typography.Text strong ellipsis={{ tooltip: mainFile.name }} className="flex-1">
                                            {mainFile.name}
                                        </Typography.Text>
                                        <Typography.Text type="secondary" className="text-xs">
                                            {_t("orders.stepped.mainFileAdded")}
                                        </Typography.Text>
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            aria-label={_t("buttons.delete")}
                                            onClick={async () => {
                                                if (mainFile?.fileId) {
                                                    await deleteFileFromServer(mainFile.fileId);
                                                }
                                                setMainFileList([]);
                                                setLastAnalyzedFileId(null);
                                            }}
                                            className="shrink-0 opacity-70 hover:opacity-100"
                                        />
                                    </Flex>
                                )}
                                <div className="h-[250px] rounded-lg flex flex-col [&_.ant-upload-list]:block [&_.ant-upload-list]:mb-2">
                                    <Dragger
                                        {...mainFileUploadProps}
                                        className="h-[250px] flex-1 [&_.ant-upload-dragger]:h-[250px] [&_.ant-upload-dragger]:flex [&_.ant-upload-dragger]:flex-col [&_.ant-upload-dragger]:items-center [&_.ant-upload-dragger]:justify-center"
                                    >
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined className="text-4xl" style={{ color: token.colorPrimary }} />
                                        </p>
                                        <p className="text-base">
                                            {mainFile
                                                ? _t("orders.stepped.replaceMainFile")
                                                : _t("orders.titles.uploadFile")}
                                        </p>
                                    </Dragger>
                                </div>
                            </Flex>
                        </Form.Item>

                        {!showAdditionalFilesPanel ? (
                            <Button
                                type="dashed"
                                icon={<InboxOutlined />}
                                onClick={() => setShowAdditionalFilesPanel(true)}
                                className="w-full"
                            >
                                {_t("orders.stepped.addMore")}
                            </Button>
                        ) : (
                            <Form.Item
                                label={
                                    <Flex gap={8} align="center">
                                        <FileTextOutlined style={{ color: token.colorPrimary }} />
                                        {_t("orders.stepped.additionalFiles")}
                                    </Flex>
                                }
                            >
                                <Flex vertical gap={8}>
                                    {additionalFiles.length > 0 && (
                                        <div className="rounded-lg border px-3 py-2" style={{ borderColor: antdToken.colorBorder, backgroundColor: antdToken.colorFillQuaternary }}>
                                            <Typography.Text type="secondary" className="text-xs block mb-1">
                                                {_t("orders.stepped.additionalFiles")} ({additionalFiles.length})
                                            </Typography.Text>
                                            {additionalFiles.map((f, i) => {
                                                const doneFiles = additionalFileList.filter(
                                                    (file) => file.status === "done" && getFileIdFromResponse(file.response)
                                                );
                                                const uploadFile = doneFiles[i];
                                                return (
                                                    <Flex
                                                        key={`${f.fileId}-${i}`}
                                                        align="center"
                                                        justify="space-between"
                                                        className="group py-0.5"
                                                    >
                                                        <Typography.Text className="truncate flex-1 min-w-0" title={f.name}>
                                                            <PaperClipOutlined style={{ color: token.colorPrimary }} className="mr-1" />
                                                            {f.name}
                                                        </Typography.Text>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            aria-label={_t("buttons.delete")}
                                                            onClick={async () => {
                                                                if (uploadFile?.uid) {
                                                                    const fileId = getFileIdFromResponse(uploadFile.response);
                                                                    if (fileId) {
                                                                        await deleteFileFromServer(fileId);
                                                                    }
                                                                    setAdditionalFileList((prev) =>
                                                                        prev.filter((file) => file.uid !== uploadFile.uid)
                                                                    );
                                                                }
                                                            }}
                                                            className="shrink-0 opacity-70 hover:opacity-100"
                                                        />
                                                    </Flex>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className="rounded-lg flex flex-col [&_.ant-upload-list]:block [&_.ant-upload-list]:mb-2">
                                        <Dragger
                                            {...additionalFilesUploadProps}
                                            className="min-h-[120px] [&_.ant-upload-dragger]:min-h-[120px] [&_.ant-upload-dragger]:flex [&_.ant-upload-dragger]:flex-col [&_.ant-upload-dragger]:items-center [&_.ant-upload-dragger]:justify-center"
                                        >
                                            <p className="ant-upload-drag-icon">
                                                <InboxOutlined className="text-2xl" style={{ color: token.colorPrimary }} />
                                            </p>
                                            <p className="text-sm">{_t("orders.stepped.uploadAdditionalFiles")}</p>
                                        </Dragger>
                                    </div>
                                </Flex>
                            </Form.Item>
                        )}
                        </Card>
                    </div>
                )}

                {/* Step 2: Vehicle information — mounted early (hidden) so analysis autofill registers fields */}
                {vehicleFormSchema.length > 0 && (
                    <div
                        className={currentStep === STEP_VEHICLE ? "block" : "hidden"}
                        aria-hidden={currentStep !== STEP_VEHICLE}
                    >
                        <Card className="shadow-lg" style={pageStyles.stepperCard}>
                            <FormRender schema={vehicleFormSchema} />
                        </Card>
                    </div>
                )}

                {/* Step 3: Stage + Options */}
                {currentStep === STEP_STAGE_OPTIONS && (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card className="shadow-lg" style={pageStyles.stepperCard}>
                                <Typography.Title level={4} className="mb-1">
                                    {_t("orders.stepped.stageSelection")}
                                </Typography.Title>
                                <Typography.Text type="secondary" className="block mb-4">
                                    {_t("orders.stepped.stageSelectionSubtitle")}
                                </Typography.Text>
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder={_t("orders.stepped.searchStage")}
                                    value={stageSearch}
                                    onChange={(e) => setStageSearch(e.target.value)}
                                    allowClear
                                    className="mb-4"
                                />
                                {productGroups.length > 0 && (
                                    <Radio.Group
                                        optionType="button"
                                        value={selectedGroup}
                                        onChange={(e) => setSelectedGroup(e.target.value)}
                                        className="mb-4 flex flex-wrap"
                                    >
                                        {productGroups.map((group) => (
                                            <Radio.Button key={group || "ungrouped"} value={group}>
                                                {group || _t("orders.stepped.group.ungrouped")}
                                            </Radio.Button>
                                        ))}
                                    </Radio.Group>
                                )}
                                <Flex vertical gap={10} className="max-h-[460px] overflow-y-auto pr-1">
                                    {filteredStages.map((p: any, index: number) => {
                                        const price = getProductPrice(p.id);
                                        const isSelected = selectedStageIds.includes(p.id);
                                        return (
                                            <Card
                                                key={p.id}
                                                size="small"
                                                className="cursor-pointer transition-colors !rounded-xl"
                                                style={
                                                    isSelected
                                                        ? {
                                                            ...pageStyles.selectableCard,
                                                            borderColor: antdToken.colorPrimary,
                                                            backgroundColor: antdToken.colorPrimaryBg,
                                                            boxShadow: `0 0 0 2px ${antdToken.colorPrimaryBorder}`,
                                                        }
                                                        : pageStyles.selectableCard
                                                }
                                                onClick={() => {
                                                    setSelectedStageIds(isSelected ? [] : [p.id]);
                                                }}
                                            >
                                                <Flex justify="space-between" align="start" gap={12}>
                                                    <div className="min-w-0">
                                                        <Flex align="center" gap={8} className="mb-1">
                                                            <Typography.Text strong className="text-base">
                                                                {p.name}
                                                            </Typography.Text>
                                                        </Flex>
                                                        <Typography.Text type="secondary" className="text-xs hidden">
                                                            {p.description || _t("orders.stepped.noStageDescription")}
                                                        </Typography.Text>
                                                    </div>
                                                    {isPrepaid && (
                                                        <Typography.Text
                                                            className={`${price === 0 ? "text-emerald-500" : ""} font-semibold whitespace-nowrap`}
                                                            style={price === 0 ? undefined : { color: antdToken.colorPrimary }}
                                                        >
                                                            {price === 0
                                                                ? _t("orders.stepped.creditsFree")
                                                                : `${price} ${_t("credits.credits")}`}
                                                        </Typography.Text>
                                                    )}
                                                </Flex>
                                            </Card>
                                        );
                                    })}
                                </Flex>
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card className="shadow-lg" style={pageStyles.stepperCard}>
                                <Typography.Title level={4} className="mb-1">
                                    {_t("orders.stepped.options")}
                                </Typography.Title>
                                <Typography.Text type="secondary" className="block mb-4">
                                    {_t("orders.stepped.optionsSubtitle")}
                                </Typography.Text>
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder={_t("orders.stepped.searchOption")}
                                    value={optionsSearch}
                                    onChange={(e) => setOptionsSearch(e.target.value)}
                                    allowClear
                                    className="mb-4"
                                />
                                <div className="max-h-[460px] overflow-y-auto space-y-5 pr-1">
                                    {filteredOptionsByGroup.map(([groupName, opts]) => (
                                        <div key={groupName || "ungrouped"}>
                                            <Flex align="center" gap={6} className="mb-2">
                                                <Typography.Text strong className="uppercase text-xs tracking-wider text-gray-600 dark:text-gray-400">
                                                    {groupName || _t("orders.stepped.options")}
                                                </Typography.Text>
                                                <InfoCircleOutlined className="text-[11px] text-gray-400" />
                                            </Flex>
                                            <Flex wrap="wrap" gap={8}>
                                                {opts.map((p: any) => {
                                                    const price = getProductPrice(p.id);
                                                    const isSelected = selectedOptionIds.includes(p.id);
                                                    return (
                                                        <Card
                                                            key={p.id}
                                                            size="small"
                                                            className="cursor-pointer w-[calc(50%-4px)] min-w-[160px] transition-colors !rounded-xl relative"
                                                            style={
                                                                isSelected
                                                                    ? {
                                                                        ...pageStyles.selectableCard,
                                                                        borderColor: antdToken.colorPrimary,
                                                                        backgroundColor: antdToken.colorPrimaryBg,
                                                                        boxShadow: `0 0 0 2px ${antdToken.colorPrimaryBorder}`,
                                                                    }
                                                                    : pageStyles.selectableCard
                                                            }
                                                            onClick={() => {
                                                                setSelectedOptionIds((prev) =>
                                                                    isSelected
                                                                        ? prev.filter((id) => id !== p.id)
                                                                        : [...prev, p.id]
                                                                );
                                                            }}
                                                        >
                                                            {isSelected && (
                                                                <CheckCircleFilled
                                                                    className="absolute right-2 top-2 text-sm"
                                                                    style={{ color: antdToken.colorPrimary }}
                                                                />
                                                            )}
                                                            <div className="font-semibold truncate pr-6">{p.name}</div>
                                                            {isPrepaid && (
                                                                <div
                                                                    className={`text-sm mt-1 ${price === 0 ? "text-emerald-500" : ""} font-medium`}
                                                                    style={price === 0 ? undefined : { color: antdToken.colorPrimary }}
                                                                >
                                                                    {price === 0
                                                                        ? _t("orders.stepped.creditsFree")
                                                                        : `${price} ${_t("credits.credits")}`}
                                                                </div>
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                            </Flex>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                        {isPrepaid && (
                            <Col span={24}>
                                <Card className="shadow-lg" style={pageStyles.stepperCard}>
                                    <Flex vertical gap={8}>
                                        <Typography.Title level={5}>{_t("orders.fields.totalAmount")}</Typography.Title>
                                        <Flex align="center" justify="space-between" gap={0} wrap="wrap">
                                            <Typography.Text type="secondary" className="text-sm">
                                                {selectedProductsBreakdown || "—"}
                                            </Typography.Text>
                                            <Typography.Title level={2} className="!m-0">
                                                {totalCredits} <span className="text-base font-normal text-gray-500">{_t("credits.credits")}</span>
                                            </Typography.Title>
                                        </Flex>
                                    </Flex>
                                    {totalCredits > accountCredit && (
                                        <Alert
                                            message={_t("orders.titles.insufficientCredit")}
                                            type="info"
                                            showIcon
                                            className="mt-2"
                                        />
                                    )}
                                </Card>
                            </Col>
                        )}
                    </Row>
                )}

                {/* Step 4: Summary + comments */}
                {currentStep === STEP_SUMMARY && (
                    <Card className="shadow-lg !rounded-2xl" style={pageStyles.stepperCard}>
                        <Flex vertical gap={24}>
                            <section className="pb-5 border-b" style={{ borderColor: pageStyles.summaryDividerColor }}>
                                <SummarySectionHeader
                                    icon={<CarOutlined />}
                                    title={_t("orders.stepped.summaryVehicle")}
                                    onEdit={() => setCurrentStep(STEP_VEHICLE)}
                                />
                                <Typography.Title level={3} className="!mb-3 !mt-3">
                                    {vehicleTitle}
                                    {vehicleYear ? ` (${vehicleYear})` : ""}
                                </Typography.Title>
                                {vehicleSpecs.length > 0 ? (
                                    <Flex wrap="wrap" gap={12}>
                                        {vehicleSpecs.map((spec) => (
                                            <Flex
                                                key={spec.key}
                                                align="center"
                                                gap={6}
                                                className="rounded-md px-2.5 py-1"
                                                style={pageStyles.summaryInnerCard}
                                            >
                                                <Typography.Text type="secondary" className="text-xs">
                                                    {spec.label}
                                                </Typography.Text>
                                                <Typography.Text strong className="text-sm">
                                                    {spec.value}
                                                </Typography.Text>
                                            </Flex>
                                        ))}
                                    </Flex>
                                ) : (
                                    <Typography.Text type="secondary">—</Typography.Text>
                                )}
                            </section>

                            <section className="pb-5 border-b" style={{ borderColor: pageStyles.summaryDividerColor }}>
                                <SummarySectionHeader
                                    icon={<SettingOutlined />}
                                    title={_t("orders.stepped.summaryServiceConfiguration")}
                                    onEdit={() => setCurrentStep(STEP_STAGE_OPTIONS)}
                                />

                                <Flex align="center" gap={8} wrap="wrap" className="mt-4 mb-4">
                                    {primaryStage ? (
                                        <Tag
                                            className="!m-0 !px-4 !py-1 !font-semibold !text-sm"
                                            style={{
                                                color: antdToken.colorPrimaryText,
                                                borderColor: antdToken.colorPrimaryBorder,
                                                background: antdToken.colorPrimaryBg,
                                            }}
                                        >
                                            {primaryStage.name}
                                        </Tag>
                                    ) : (
                                        <Typography.Text type="secondary">—</Typography.Text>
                                    )}
                                    {remainingStages.map((stage) => (
                                        <Tag
                                            key={stage.id}
                                            className="!m-0 !px-3 !py-1"
                                            style={{
                                                color: antdToken.colorPrimaryText,
                                                borderColor: antdToken.colorPrimaryBorder,
                                                background: antdToken.colorPrimaryBg,
                                            }}
                                        >
                                            {stage.name}
                                        </Tag>
                                    ))}
                                </Flex>

                                <Typography.Text type="secondary" className="uppercase tracking-wider text-xs block mb-3">
                                    {_t("orders.stepped.summaryOptions")}
                                </Typography.Text>
                                {selectedOptions.length > 0 ? (
                                    <Flex wrap="wrap" gap={10} className="mb-4">
                                        {selectedOptions.map((option) => (
                                            <Tag key={option.id} className="!m-0 !px-3 !py-1.5 !rounded-md">
                                                <Flex align="center" gap={6}>
                                                    <CheckOutlined className="text-xs" style={{ color: antdToken.colorPrimary }} />
                                                    <span>{option.name}</span>
                                                </Flex>
                                            </Tag>
                                        ))}
                                    </Flex>
                                ) : (
                                    <Typography.Text type="secondary" className="block mb-4">—</Typography.Text>
                                )}

                                {isPrepaid && (
                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Flex justify="space-between" align="center">
                                            <Typography.Title level={5}>{_t("orders.fields.totalAmount")}</Typography.Title>
                                            <Typography.Title level={3} className="!m-0" style={{ color: antdToken.colorPrimary }}>
                                                {totalCredits} {_t("credits.credits")}
                                            </Typography.Title>
                                        </Flex>
                                        {totalCredits > accountCredit && (
                                            <Alert
                                                message={_t("orders.titles.insufficientCredit")}
                                                type="info"
                                                showIcon
                                                className="mt-3"
                                            />
                                        )}
                                    </div>
                                )}
                            </section>

                            <section className="pb-5 border-b" style={{ borderColor: pageStyles.summaryDividerColor }}>
                                <SummarySectionHeader
                                    icon={<ToolOutlined />}
                                    title={_t("orders.stepped.summaryTechnicalDetails")}
                                    onEdit={() => setCurrentStep(STEP_FILE)}
                                />
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                                    <div
                                        className="rounded-lg px-4 py-3 col-span-2 lg:col-span-3"
                                        style={pageStyles.summaryInnerCard}
                                    >
                                        <Flex align="start" gap={10}>
                                            <FileTextOutlined className="mt-1" style={{ color: antdToken.colorPrimary }} />
                                            <Flex vertical gap={2} className="min-w-0">
                                                <Typography.Text type="secondary" className="text-xs">
                                                    {_t("orders.stepped.mainFile")}
                                                </Typography.Text>
                                                <Typography.Text strong ellipsis={{ tooltip: primaryFile?.name || "—" }}>
                                                    {primaryFile?.name || "—"}
                                                </Typography.Text>
                                                {additionalFilesCount > 0 && (
                                                    <Typography.Text type="secondary" className="text-xs">
                                                        {_t("orders.stepped.additionalFiles")}: {additionalFilesCount}{" "}
                                                        {_t("orders.stepped.summaryFiles").toLowerCase()}
                                                    </Typography.Text>
                                                )}
                                            </Flex>
                                        </Flex>
                                    </div>

                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Typography.Text type="secondary" className="block text-xs mb-1">
                                            {_t("orders.stepped.readingMethod")}
                                        </Typography.Text>
                                        <Typography.Text strong>{selectedMethodLabel}</Typography.Text>
                                    </div>

                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Typography.Text type="secondary" className="block text-xs mb-1">
                                            {_t("orders.stepped.technique")}
                                        </Typography.Text>
                                        <Typography.Text strong>{selectedTechniqueLabel}</Typography.Text>
                                    </div>

                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Typography.Text type="secondary" className="block text-xs mb-1">
                                            {_t("orders.stepped.masterSlave")}
                                        </Typography.Text>
                                        <Typography.Text strong>{selectedMasterSlaveLabel}</Typography.Text>
                                    </div>

                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Typography.Text type="secondary" className="block text-xs mb-1">
                                            {_t("orders.stepped.calculator")}
                                        </Typography.Text>
                                        <Typography.Text strong>{form.getFieldValue("calculator") ?? "—"}</Typography.Text>
                                    </div>

                                    <div className="rounded-lg px-4 py-3" style={pageStyles.summaryInnerCard}>
                                        <Typography.Text type="secondary" className="block text-xs mb-1">
                                            {_t("orders.stepped.prepared")}
                                        </Typography.Text>
                                        <Typography.Text strong>{form.getFieldValue("prepared") ?? "—"}</Typography.Text>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <div className="mb-3">
                                    <SummarySectionHeader
                                        icon={<MessageOutlined />}
                                        title={_t("orders.stepped.specialInstructions")}
                                    />
                                </div>
                                <Form.Item className="!mb-2">
                                    <Input.TextArea
                                        rows={4}
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        placeholder={_t("orders.stepped.specialInstructionsPlaceholder")}
                                    />
                                </Form.Item>
                                <Typography.Text type="secondary" className="text-xs">
                                    {_t("orders.stepped.specialInstructionsHelp")}
                                </Typography.Text>
                            </section>
                        </Flex>
                    </Card>
                )}
            </Form>

            {currentStep < STEP_SUMMARY ? (
                <Flex className="bottom-3 z-30 mt-6 py-3 rounded-xl" gap={12}>
                    <Button
                        disabled={currentStep === 0 || (currentStep === STEP_FILE && isMainFileBusy)}
                        onClick={() => setCurrentStep((s) => s - 1)}
                        className="min-w-[120px] !h-11"
                    >
                        {_t("orders.stepped.back")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={
                            (currentStep === STEP_FILE && (!canGoNextStep1 || isMainFileBusy)) ||
                            (currentStep === STEP_STAGE_OPTIONS && !canGoNextStepStageOptions)
                        }
                        loading={isValidatingStep}
                        className="flex-1 !h-11 text-base font-semibold !rounded-lg"
                        onClick={handleNextStep}
                    >
                        {_t("orders.stepped.next")}
                    </Button>
                </Flex>
            ) : (
                <Flex className="bottom-3 z-30 mt-6 py-3 rounded-xl" gap={12}>
                    <Button
                        size="large"
                        onClick={() => setCurrentStep((s) => s - 1)}
                        className="min-w-[120px] !h-11"
                    >
                        {_t("orders.stepped.back")}
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<SendOutlined />}
                        disabled={!canSubmit}
                        loading={isSendingOrder}
                        onClick={handleSubmit}
                        className="flex-1 !h-11 text-base font-semibold !rounded-lg"
                    >
                        {_t("orders.titles.sendOrder")}
                    </Button>
                </Flex>
            )}
        </Flex>
    );
};
